import type { EventClickArg, EventDropArg } from '@fullcalendar/core';
import type { EventResizeDoneArg } from '@fullcalendar/interaction';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AddIcon from '@mui/icons-material/Add';
import { Avatar, Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';

import HoursSummaryDialog from './HoursSummaryDialog.js';
import RosterCalendar from './RosterCalendar.js';
import { colorForUser } from './userColor.js';
import { useDeleteShift, useShifts, useUpdateShift, useUsers } from '../../api/index.js';
import useSnackBar from '../../hooks/use-snack-bar.js';
import ShiftCreateModal, { type ShiftCreateModalProps } from '../Shifts/ShiftCreateModal.js';
import ShiftDeleteConfirmDialog from '../Shifts/ShiftDeleteConfirmDialog.js';
import ShiftScopeDialog from '../Shifts/ShiftScopeDialog.js';

type EditTarget = NonNullable<ShiftCreateModalProps['editTarget']>;

type DragScopeState = {
  revert: () => void;
  occurrenceId: string;
  userId: string;
  newStart: string;
  newEnd: string;
  action: 'drop' | 'resize';
};

const DRAG_SCOPE_OPTIONS = [
  { value: 'this', label: 'This occurrence' },
  { value: 'all-future', label: 'This & all future' },
];

const RosterPanel = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [prefill, setPrefill] = useState<{ start: Date; end: Date; monitorId: string | null } | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editTarget, setEditTarget] = useState<EditTarget | undefined>(undefined);
  const [dragScope, setDragScope] = useState<DragScopeState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ occurrenceId: string; isRecurring: boolean } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ occurrenceId: string; isRecurring: boolean } | null>(null);
  const [range, setRange] = useState<{ start: string; end: string } | null>(null);
  const [summaryOpen, setSummaryOpen] = useState(false);

  const { data: monitors = [], isLoading: monitorsLoading } = useUsers();
  const shiftsQuery = useShifts(range?.start, range?.end);
  const shifts = shiftsQuery.data ?? [];
  const snackBar = useSnackBar();
  const { mutateAsync: updateShift, isLoading: isDragUpdating } = useUpdateShift();
  const { mutateAsync: deleteShift, isLoading: isDeleting } = useDeleteShift();

  const currentEvents = useMemo(
    () =>
      shifts.map((s) => ({
        resourceId: s.userId,
        start: new Date(s.start),
        end: new Date(s.end),
      })),
    [shifts],
  );

  const onDutyNow = useMemo(() => {
    const now = Date.now();
    const activeIds = new Set(
      shifts
        .filter((s) => new Date(s.start).getTime() <= now && now <= new Date(s.end).getTime())
        .map((s) => s.userId),
    );
    return monitors.filter((m) => activeIds.has(m.id));
  }, [shifts, monitors]);

  const openNewShift = () => {
    setPrefill(null);
    setEditTarget(undefined);
    setModalMode('create');
    setModalOpen(true);
  };

  const handleModalClose = () => {
    // Only request close here; mode/editTarget reset is deferred to onExited so the
    // modal doesn't visibly morph (edit -> create) while it fades out.
    setModalOpen(false);
  };

  const handleEventClick = (arg: EventClickArg) => {
    const props = arg.event.extendedProps as { isRecurring: boolean; occurrenceId: string; ruleId: string | null };
    setEditTarget({
      occurrenceId: props.occurrenceId,
      ruleId: props.ruleId ?? '',
      isRecurring: props.isRecurring,
      start: arg.event.start!,
      end: arg.event.end!,
      monitorId: arg.event.getResources()[0]?.id ?? '',
    });
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleEventDrop = (arg: EventDropArg) => {
    const props = arg.event.extendedProps as { isRecurring: boolean; occurrenceId: string };
    const userId = arg.event.getResources()[0]?.id ?? '';
    const newStart = arg.event.start!.toISOString();
    const newEnd = (arg.event.end ?? arg.event.start!).toISOString();

    if (!props.isRecurring) {
      void updateShift({ occurrenceId: props.occurrenceId, userId, start: newStart, end: newEnd, scope: 'this' })
        .catch(() => { arg.revert(); snackBar.error('Failed to update shift. Please try again.'); });
      return;
    }

    // Capture revert SYNCHRONOUSLY before any async work (Pitfall 3)
    setDragScope({ revert: arg.revert, occurrenceId: props.occurrenceId, userId, newStart, newEnd, action: 'drop' });
  };

  const handleEventResize = (arg: EventResizeDoneArg) => {
    const props = arg.event.extendedProps as { isRecurring: boolean; occurrenceId: string };
    const userId = arg.event.getResources()[0]?.id ?? '';
    const newStart = arg.event.start!.toISOString();
    const newEnd = arg.event.end!.toISOString();

    if (!props.isRecurring) {
      void updateShift({ occurrenceId: props.occurrenceId, userId, start: newStart, end: newEnd, scope: 'this' })
        .catch(() => { arg.revert(); snackBar.error('Failed to update shift. Please try again.'); });
      return;
    }

    // Capture revert SYNCHRONOUSLY before any async work (Pitfall 3)
    setDragScope({ revert: arg.revert, occurrenceId: props.occurrenceId, userId, newStart, newEnd, action: 'resize' });
  };

  const handleDragApply = (scope: string) => {
    if (!dragScope) {
      return;
    }
    const { occurrenceId, userId, newStart, newEnd, revert } = dragScope;
    void updateShift({ occurrenceId, userId, start: newStart, end: newEnd, scope: scope as 'this' | 'all-future' })
      .then(() => {
        setDragScope(null);
        snackBar.success('Shift updated.');
      })
      .catch(() => {
        revert(); // Only revert on error, never on success (Pitfall 7)
        snackBar.error('Failed to update shift. Please try again.');
        setDragScope(null);
      });
  };

  const handleDragCancel = () => {
    if (dragScope) {
      dragScope.revert(); // Revert on cancel
      setDragScope(null);
    }
  };

  const handleDeleteClick = () => {
    if (!editTarget) {
      return;
    }
    // Keep the modal showing its edit content while it closes; the mode/editTarget reset and
    // the delete-dialog open both run in onExited, after the modal has fully closed — otherwise
    // the closing modal visibly morphs edit -> create (and flashes the overlap warning).
    setPendingDelete({ occurrenceId: editTarget.occurrenceId, isRecurring: editTarget.isRecurring });
    setModalOpen(false);
  };

  const handleModalExited = () => {
    setEditTarget(undefined);
    setModalMode('create');
    if (pendingDelete) {
      setDeleteTarget(pendingDelete);
      setPendingDelete(null);
    }
  };

  const handleDeleteConfirm = (scope: 'this' | 'following') => {
    if (!deleteTarget) {
      return;
    }
    void deleteShift({ occurrenceId: deleteTarget.occurrenceId, scope })
      .then(() => {
        setDeleteTarget(null);
        snackBar.success(scope === 'following' ? 'Shifts deleted.' : 'Shift deleted.');
      })
      .catch(() => {
        snackBar.error('Failed to delete shift. Please try again.');
      });
  };

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
              On duty now
            </Typography>
            <Typography variant="h6" sx={{ lineHeight: 1 }}>
              {onDutyNow.length}
            </Typography>
          </Box>
          {onDutyNow.length > 0 ? (
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {onDutyNow.map((m) => (
                <Tooltip key={m.id} title={m.name}>
                  <Avatar
                    src={m.picture}
                    alt={m.name}
                    sx={{ width: 32, height: 32, fontSize: 13, border: `2px solid ${colorForUser(m.id)}` }}
                  >
                    {m.name.charAt(0)}
                  </Avatar>
                </Tooltip>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No one is on duty right now.
            </Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<AccessTimeOutlinedIcon />} onClick={() => setSummaryOpen(true)}>
            Hours summary
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNewShift}>
            New shift
          </Button>
        </Stack>
      </Stack>

      {!monitorsLoading && shifts.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No shifts in this range yet — drag a block in a monitor&apos;s lane, or use New shift.
        </Typography>
      )}

      <RosterCalendar
        monitors={monitors}
        monitorsLoading={monitorsLoading}
        shifts={shifts}
        onSelect={(arg) => {
          setPrefill({ start: arg.start, end: arg.end, monitorId: arg.resource?.id ?? null });
          setModalMode('create');
          setEditTarget(undefined);
          setModalOpen(true);
        }}
        onEventClick={handleEventClick}
        onEventDrop={handleEventDrop}
        onEventResize={handleEventResize}
        onDatesSet={(start, end) => setRange({ start, end })}
      />

      <ShiftCreateModal
        open={modalOpen}
        onClose={handleModalClose}
        prefill={prefill}
        monitors={monitors}
        currentEvents={currentEvents}
        mode={modalMode}
        editTarget={editTarget}
        onDelete={handleDeleteClick}
        onExited={handleModalExited}
      />

      <ShiftScopeDialog
        open={dragScope !== null}
        title={dragScope?.action === 'resize' ? 'Update shift' : 'Move shift'}
        options={DRAG_SCOPE_OPTIONS}
        defaultValue="this"
        actionLabel="Apply"
        loading={isDragUpdating}
        onConfirm={handleDragApply}
        onCancel={handleDragCancel}
      />

      <ShiftDeleteConfirmDialog
        open={deleteTarget !== null}
        isRecurring={deleteTarget?.isRecurring ?? false}
        loading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />

      <HoursSummaryDialog open={summaryOpen} onClose={() => setSummaryOpen(false)} range={range} />
    </Box>
  );
};

export default RosterPanel;
